import type { ReactNode } from 'react';
import { Minus, Plus, Scan } from 'lucide-react';
import { cn, IconButton } from '@/components/ui';
import { formatFixed } from '@/logic/statistics';
import { formatDistance, formatMultiplier, niceDistance } from './geometry';
import { unitSuffix } from './mapDisplay';

/** Floating surface shared by every overlay control on the map. */
export const CHROME =
  'rounded-lg border border-border bg-surface-raised/90 shadow-sm backdrop-blur-sm';

export const PaneLabel = ({ children }: { children: ReactNode }) => (
  <div
    className={cn(
      CHROME,
      'pointer-events-none absolute left-2 top-2 flex max-w-[calc(100%-4.5rem)] items-center gap-1.5 truncate px-2 py-1 text-xs font-medium text-fg'
    )}
  >
    {children}
  </div>
);

/** North is always up: plane coordinates are drawn without rotation. */
export const NorthArrow = () => (
  <div
    role="img"
    aria-label="North is up"
    className={cn(
      CHROME,
      'pointer-events-none absolute right-2 top-2 flex size-9 flex-col items-center justify-center rounded-full'
    )}
  >
    <svg width="14" height="22" viewBox="-7 -11 14 22" aria-hidden="true">
      <text
        x="0"
        y="-3.5"
        textAnchor="middle"
        className="fill-fg-muted text-[9px] font-semibold"
      >
        N
      </text>
      <path d="M0 -1.5 4.25 10 0 7.25Z" className="fill-fg-muted/30" />
      <path d="M0 -1.5 -4.25 10 0 7.25Z" className="fill-fg" />
      <path
        d="M0 -1.5 4.25 10 0 7.25 -4.25 10Z"
        className="fill-none stroke-fg"
        strokeWidth={0.75}
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/**
 * Scale bar in ground units (units are labels only) and, when vectors are
 * drawn, their exaggeration relative to the map scale.
 */
export const ScaleBar = ({
  pixelsPerUnit,
  paneWidth,
  units,
  vectorMultiplier,
  vectorLabel = 'Residuals',
  largestMovement,
}: {
  pixelsPerUnit: number;
  paneWidth: number;
  units?: string;
  /** Exaggeration of residual vectors and uncertainty circles, if drawn. */
  vectorMultiplier?: number | null;
  vectorLabel?: string;
  /** Largest true-scale fit movement while comparing fits. */
  largestMovement?: number | null;
}) => {
  if (!Number.isFinite(pixelsPerUnit) || pixelsPerUnit <= 0) return null;
  const target = Math.min(120, Math.max(56, paneWidth * 0.16));
  const ground = niceDistance(target / pixelsPerUnit);
  const barPx = ground * pixelsPerUnit;
  const suffix = unitSuffix(units);
  const label = `${formatDistance(ground)}${suffix ? ` ${suffix}` : ''}`;
  return (
    <div
      className={cn(
        CHROME,
        'pointer-events-none absolute bottom-2 left-2 flex flex-col gap-1 px-2 py-1.5'
      )}
    >
      {vectorMultiplier != null && vectorMultiplier > 0 && (
        <span className="flex items-center gap-1 text-[11px] font-medium leading-none text-residual">
          <svg width="14" height="8" viewBox="0 0 14 8" aria-hidden="true">
            <path
              d="M1 4h9.5"
              className="stroke-residual"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
            <path d="M9 1 13 4 9 7Z" className="fill-residual" />
          </svg>
          {vectorLabel} {formatMultiplier(vectorMultiplier)}
        </span>
      )}
      {largestMovement != null && (
        <span className="flex items-center gap-1 text-[11px] font-medium leading-none text-record">
          <svg width="14" height="8" viewBox="0 0 14 8" aria-hidden="true">
            <path
              d="M1.5 4h11"
              className="stroke-record"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          </svg>
          Movement ≤{' '}
          <span className="font-mono">
            {formatFixed(largestMovement, 3)}
            {suffix && ` ${suffix}`}
          </span>
        </span>
      )}
      <span className="flex flex-col gap-1">
        <span
          className="font-mono text-[11px] leading-none text-fg-muted"
          data-scale-label
        >
          {label}
        </span>
        <span
          aria-hidden="true"
          className="flex h-1.5 overflow-hidden rounded-[2px] border border-fg"
          style={{ width: barPx }}
        >
          <span className="h-full w-1/2 bg-fg" />
          <span className="h-full w-1/2 bg-surface" />
        </span>
      </span>
    </div>
  );
};

export const ZoomControls = ({
  zoom,
  onZoomIn,
  onZoomOut,
  onFit,
  canZoomIn,
  canZoomOut,
}: {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
}) => (
  <div
    className={cn(
      CHROME,
      'absolute bottom-2 right-2 flex items-center gap-0.5 p-0.5'
    )}
  >
    <IconButton
      aria-label="Zoom out"
      size="sm"
      icon={<Minus />}
      onClick={onZoomOut}
      disabled={!canZoomOut}
    />
    <span className="min-w-10 text-center font-mono text-[11px] tabular-nums text-fg-muted">
      {Math.round(zoom * 100)}%
    </span>
    <IconButton
      aria-label="Zoom in"
      size="sm"
      icon={<Plus />}
      onClick={onZoomIn}
      disabled={!canZoomIn}
    />
    <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-border" />
    <IconButton
      aria-label="Zoom to fit"
      size="sm"
      icon={<Scan />}
      onClick={onFit}
    />
  </div>
);
